import { useEffect, useRef } from "react";
import { useState } from "react";
/** Custom Hooks */
import useDebounce from "../../app/customHooks/useDebounce";
/** Common Components */
import CardSection from "../../components/ui/card/CardSection";
import CenterModel from "../../components/ui/centerModel/CenterModel";
import { fabClasses } from "@mui/material";
/** Common Services & Data Files */
import ToastService from "../../services/toastService/ToastService";
import SwalAlert from "../../services/swalService/SwalService";
import AddEditPlanningStatus from "./features/AddEditPlanningStatus";
import { planningStatusGridConfig } from "./features/config/planningStatus.data";
import FinalMolGrid from "../../components/FinalMolGrid/FinalMolGrid";
import {
    useDeletePlanningStatusMutation,
    useGetAllPlanningStatusListMutation
} from "../../app/services/planningStatusAPI";

const PlanningStatus = () => {
    const molGridRef = useRef();
    const { confirm } = SwalAlert();
    /* State*/
    const [searchText, setSearchText] = useState("");
    const debouncedSearch = useDebounce(searchText, 300);
    const [showModal, setShowModal] = useState(false);
    const [listData, setListData] = useState([]);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [initData, setInitData] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
    });
    // API'S Call
    const [GetAllPlanningStatus, {
        isLoading: isGetAllPlanningStatusLoading,
        isSuccess: isGetAllPlanningStatusSuccess,
        data: getAllPlanningStatusData
    }] = useGetAllPlanningStatusListMutation();
    const [deletePlanningStatus, {
        isSuccess: isDeleteSuccess,
        isLoading: isDeleteLoading,
        data: isDeletData
    },] = useDeletePlanningStatusMutation();

    //** useEffect's */
    useEffect(() => {
        if (isGetAllPlanningStatusSuccess && getAllPlanningStatusData) {
            setListData(getAllPlanningStatusData.dataSource);
            setTotalRowCount(getAllPlanningStatusData.totalRecord);
        }
    }, [isGetAllPlanningStatusSuccess, getAllPlanningStatusData]);

    useEffect(() => {
        if (isDeleteSuccess && isDeletData) {
            ToastService.success(isDeletData.errorMessage);
            const currentPageObject = molGridRef.current.getCurrentPageObject();
            getPlanningStatusList(currentPageObject);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDeleteSuccess, isDeletData]);

    useEffect(() => {
        if (molGridRef.current) {
            const currentPageObject = molGridRef.current.getCurrentPageObject();
            getPlanningStatusList(currentPageObject);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    //** Funcation's */

    const handleOnSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const getPlanningStatusList = (pageObject) => {
        const request = {
            pagination: { pageNumber: pageObject.pageNumber, pageSize: pageObject.pageSize },
            filters: { searchText: debouncedSearch },
        };
        GetAllPlanningStatus(request);
    };

    const handlePageChange = (pageObject) => {
        setPagination((prev) => ({
            ...prev,
            pageNumber: pageObject.pageNumber,
            pageSize: pageObject.pageSize,
        }));
        getPlanningStatusList(pageObject);
    };

    const onEditAction = (data) => {
        setInitData(data);
        setShowModal(true);
        setIsEdit(true);
    };

    const handleSuccess = () => {
        setShowModal(false);
        setListData(null)
        const currentPageObject = molGridRef.current.getDefulatPageObject();
        getPlanningStatusList(currentPageObject);
    };

    const handlePlanningStatusModal = () => {
        setInitData(null);
        setShowModal(!showModal);
        setIsEdit(false)
        if (molGridRef.current) {
            const currentPageObject = molGridRef.current.getCurrentPageObject();
            const request = {
                pagination: {
                    pageNumber: currentPageObject.pageNumber,
                    pageSize: currentPageObject.pageSize,
                },
                filters: { searchText: debouncedSearch || "" },
            };
            getPlanningStatusList(request);
        }
    };

    const handleDeleteClick = (data) => {
        confirm(
            "Delete?",
            "Are you sure you want to delete this Planning Status?",
            "Delete",
            "Cancel"
        ).then((confirmed) => {
            if (confirmed) {
                deletePlanningStatus(data.planningStatusId);
            }
        });
    };

    const actionHandler = {
        DELETE: handleDeleteClick,
        EDIT: onEditAction,
    };

    return (
        <>
            <div className="source-control-list center-top-space">
                <CardSection
                    isContentLoading={isGetAllPlanningStatusLoading}
                    cardTitle={"Planning Status List"}
                    rightButton={true}
                    titleButtonClick={handlePlanningStatusModal}
                    buttonClassName="theme-btn"
                    textWithIcon={true}
                    iconName="material-symbols:add-rounded"
                    fontIcon={true}
                    buttonText="Add Planning Status"
                    searchInputName="Search by Planning Status"
                    searchInput={fabClasses}
                    handleChange={handleOnSearchChange}>
                    <div className="table-grid">
                        <FinalMolGrid
                            ref={molGridRef}
                            configuration={planningStatusGridConfig}
                            dataSource={listData}
                            allowPagination={true}
                            pagination={{
                                totalCount: totalRowCount,
                                pageSize: pagination.pageSize,
                                currentPage: pagination.pageNumber,
                            }}
                            isLoading={isGetAllPlanningStatusLoading || isDeleteLoading}
                            onActionChange={actionHandler}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </CardSection>
            </div>
            <div>
                {showModal && (
                    <CenterModel
                        showModal={showModal}
                        handleToggleModal={handlePlanningStatusModal}
                        modalTitle={isEdit ? "Update Planning Status" : "Add Planning Status"}
                        modelSizeClass="w-40"
                    >
                        <div className="row">
                            <AddEditPlanningStatus
                                onClose={handlePlanningStatusModal}
                                planningStatusData={initData}
                                isEdit={isEdit}
                                onSuccess={handleSuccess}
                            />
                        </div>
                    </CenterModel>
                )}
            </div>
        </>
    );
};

export default PlanningStatus;